import type { ReactNode } from 'react';

import { BrandMark } from '@/components/BrandMark';
import { palette } from '@/theme';

/**
 * The template's `*-illustration` auth layout: a brand panel on the left, the form on the right,
 * one column below `lg`.
 *
 * Extracted because two screens now wear it — sign-in and reset-password — and the template ships
 * it as `loginIllustration`, `resetPasswordIllustration`, `forgotPasswordIllustration` and three
 * more, each a copy of the same forty lines. Copying it a second time here is how the gradient, the
 * breakpoint and the wordmark rule end up disagreeing between two screens that are meant to read as
 * one flow.
 *
 * The "illustration" is a Phosphor duotone glyph on a gradient rather than an image — that is how
 * the template draws it, and it is why these screens need no asset, no `public/` file and no
 * network request. The gradient runs `--color-primary` to `--color-success`, both already brand.
 */
export interface AuthPanel {
  /** A Phosphor duotone class pair, e.g. `ph-duotone ph-lock-key`. */
  icon: string;
  heading: string;
  blurb: string;
}

export function AuthLayout({
  panel,
  title,
  subtitle,
  children,
}: {
  panel: AuthPanel;
  title: string;
  subtitle: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="bg-light min-h-screen">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
        {/*
          `hidden lg:flex` keeps the panel off narrow screens entirely: on a counter tablet in
          portrait the form should have the whole viewport, not compete with decoration for it.
        */}
        <div className="hidden items-center justify-center bg-gradient-to-br from-primary to-success p-8 lg:flex">
          <div className="max-w-sm text-center text-white">
            {/* aria-hidden: decorative. The heading beside it already carries the meaning. */}
            <i className={`${panel.icon} mb-4 text-8xl`} aria-hidden="true" />
            <h1 className="mb-2 text-2xl font-bold text-white">{panel.heading}</h1>
            <p className="text-sm opacity-90">{panel.blurb}</p>
          </div>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="mb-6 text-center">
              {/*
                Only below `lg`, where the brand panel is not on screen. Above it the panel already
                says AttireRent an inch to the left, and repeating it twice on one screen reads as a
                mistake — but dropping it outright would leave a narrow counter tablet, which never
                sees the panel, with no branding at all.
              */}
              <div className="mb-4 lg:hidden">
                <Wordmark />
              </div>

              <h2 className="mb-1 text-xl font-bold text-title">{title}</h2>
              <p className="text-sm text-default">{subtitle}</p>
            </div>

            {/*
              There is deliberately no `Card` around the form. The illustration variant sits the
              fields directly on the canvas; keeping the card would produce a panel inside a panel.
            */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The same mark the shell uses, so the auth screens and the app agree about what this is. */
export function Wordmark() {
  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
    >
      <BrandMark size={34} tone={palette.teal} />
      {/* `text-title` rather than `palette.ink`: the latter is a fixed slate and vanishes on dark. */}
      <span
        className="text-title"
        style={{ fontWeight: 700, fontSize: 20, letterSpacing: '-0.02em' }}
      >
        AttireRent
      </span>
    </div>
  );
}
